import clsx from "clsx";

import { CircularProgress, PrizeWithCheckMark } from "@chef/components";
import { ITellAFriendCompetition } from "@chef/feature-sanity";

import { useCompetitionResultsData } from "../../../hooks/useCompetitionResultsData";
import { getTimeLeftUntilEndDate } from "../../../utils";

import { intl } from "./CircularProgressWithStatus.Intl";

interface CircularProgressWithStatusProps {
  competitionSetting: ITellAFriendCompetition["competitionSetting"];
  competitionStatusText: ITellAFriendCompetition["competitionStatusText"];
}

export const CircularProgressWithStatus = ({
  competitionSetting,
}: CircularProgressWithStatusProps) => {
  const { dateSetting, competitionRequirements } = competitionSetting;
  const { startDate, endDate, deliveryCutoffDate } = dateSetting;
  const { invitesRequired, deliveriesRequired } = competitionRequirements;

  const {
    competitionResults: { isCompetitionLost, isCompetitionWon },
    progress: { invitesProgress, deliveriesProgress },
  } = useCompetitionResultsData({
    invitesRequired,
    deliveriesRequired,
    startDate,
    endDate,
    deliveryCutoffDate,
  });

  const { formattedTimeLeft } = getTimeLeftUntilEndDate({
    endDate: deliveryCutoffDate,
  });

  let competitionStatus;

  if (isCompetitionWon) {
    competitionStatus = intl.YOU_HAVE_WON;
  } else if (isCompetitionLost) {
    competitionStatus = intl.COMPETITION_HAS_ENDED;
  } else {
    competitionStatus = formattedTimeLeft
      ? `${intl.DAYS_LEFT(formattedTimeLeft)}`
      : "";
  }

  const competitionProgress = {
    current: deliveriesProgress,
    projected: invitesProgress,
  };

  return (
    <CircularProgress progress={competitionProgress} className="mx-auto w-44">
      <div className="flex flex-col items-center text-sm">
        <PrizeWithCheckMark
          width={120}
          height={120}
          completed={isCompetitionWon}
          hasCompetitionEnded={isCompetitionLost}
        />
        <div
          className={clsx(
            isCompetitionWon ? "text-secondary" : "text-error",
            "absolute text-sm bottom-0",
          )}
        >
          <strong>{competitionStatus}</strong>
        </div>
      </div>
    </CircularProgress>
  );
};
